/**
 * offlineMutationQueue.js — Queues non-recording API mutations
 * (e.g. transcript edit save, patient create) when the network is
 * unavailable, then drains them on reconnect.
 *
 * Recording chunks have their own dedicated queue (chunkQueue.js)
 * because they have unique constraints (large blobs, ordered by
 * chunkIndex per session). This queue is for small JSON requests.
 */
const DB_NAME = 'pura_mutations';
const DB_VERSION = 1;
const STORE = 'mutations';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const mid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export async function enqueueMutation({ method, url, body, token, label }) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readwrite').objectStore(STORE).put({
      id: mid(),
      method: (method || 'POST').toUpperCase(),
      url,
      body: body ?? null,
      token,
      label: label || `${method} ${url}`,
      createdAt: Date.now(),
      attempts: 0,
    });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function listMutations() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function countMutations() {
  return (await listMutations()).length;
}

async function removeMutation(id) {
  const db = await openDB();
  return new Promise((resolve) => {
    const req = db.transaction(STORE, 'readwrite').objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
}

async function bumpMutation(id) {
  const db = await openDB();
  return new Promise((resolve) => {
    const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const rec = getReq.result;
      if (!rec) return resolve();
      rec.attempts = (rec.attempts || 0) + 1;
      const putReq = store.put(rec);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => resolve();
    };
    getReq.onerror = () => resolve();
  });
}

const MAX_ATTEMPTS = 8;
let draining = false;

export async function drainMutations({ onChange } = {}) {
  if (draining) return { drained: 0, remaining: undefined, skipped: true };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { drained: 0, remaining: undefined, skipped: true };
  }
  draining = true;
  let drained = 0;
  try {
    const list = await listMutations();
    list.sort((a, b) => a.createdAt - b.createdAt);
    for (const rec of list) {
      if ((rec.attempts || 0) >= MAX_ATTEMPTS) {
        await removeMutation(rec.id);
        onChange?.({ id: rec.id, status: 'dropped', label: rec.label });
        continue;
      }
      try {
        const res = await fetch(rec.url, {
          method: rec.method,
          headers: {
            'Content-Type': 'application/json',
            ...(rec.token ? { Authorization: `Bearer ${rec.token}` } : {}),
          },
          body: rec.body != null ? JSON.stringify(rec.body) : undefined,
        });
        if (res.ok) {
          await removeMutation(rec.id);
          drained += 1;
          onChange?.({ id: rec.id, status: 'done', label: rec.label });
        } else if (res.status === 401 || res.status === 403) {
          // Auth invalid for this queued mutation — don't keep trying.
          await removeMutation(rec.id);
          onChange?.({ id: rec.id, status: 'dropped', label: rec.label });
        } else {
          await bumpMutation(rec.id);
          onChange?.({ id: rec.id, status: 'retry', label: rec.label });
        }
      } catch {
        await bumpMutation(rec.id);
        onChange?.({ id: rec.id, status: 'retry', label: rec.label });
      }
    }
    const remaining = (await listMutations()).length;
    return { drained, remaining, skipped: false };
  } finally {
    draining = false;
  }
}
